import { useEffect, useRef, useState } from 'react';
import { removeAppCookies, removeSingleCookie, setCookie, setSessionStorage } from '../../utils/cookies-util';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Input, Button } from '../../common';
import { Checkbox, Divider, Flex } from 'antd';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { getErrorString, currentBaseRoute, userType } from '../../utils';
import { useDispatch, useSelector } from 'react-redux';
import Authwrapper from './authwrapper';
import { USER_FORGOT_PASSWORD_PATH, USER_SIGNUP_PATH } from '../../constants/user-paths';
import { loginUserApi } from '../../api/auth-user-api';
import { removePatientUser, setPatientUser } from '../../reducers/patient-user-slice';
import GoogleSignIn from '../../components/google-signin';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import ResendCode from './resendCode';
import { selectCurrentTenant } from 'reducers/selectors';
import { regex } from 'constants/generic-regex';

export default function SigninUser({ handleLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const formikRef = useRef();
  const { tenantData } = useSelector(selectCurrentTenant);
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState('');

  const params = new URLSearchParams(window.location.search);
  const affiliate_token = params.get('ref_token') ?? params.get('affiliate_token');

  const fromPage =
    location.state?.from?.pathname &&
    location.state.from.pathname !== '/' &&
    location.state?.from?.pathname?.includes(userType())
      ? location.state.from.pathname
      : currentBaseRoute();

  useEffect(() => {
    if (affiliate_token) {
      setSessionStorage('affiliate_token', affiliate_token);
    }
    removeAppCookies(true);
    dispatch(removePatientUser());
  }, []);

  const getValidationSchema = () =>
    yup.object().shape({
      email: yup.string().required('Email is required').email('Valid Email is required'),
      password: yup
        .string()
        .min(8, 'Password must be at least 8 characters')
        .max(64, 'Password must not exceed 64 characters')
        .matches(regex.password, 'Password must contain only letters, numbers, and symbols. Spaces are not allowed.')
        .required('Password is required'),
    });

  const getInitialValues = () => ({
    email: '',
    password: '',
  });

  const onSubmit = async (values, { setSubmitting, setStatus }) => {
    setSubmitting(true);
    const data = { client_user: { email: values?.email || '', password: values?.password || '' } };
    try {
      const res = await loginUserApi(data);
      if (res && res?.status) {
        dispatch(setPatientUser(res?.data?.data ?? {}));
        setCookie('access-token-user', res.headers['authorization']);
        removeSingleCookie('resend-time');
        removeSingleCookie('resend-link-time');
        handleLogin();
        navigate(fromPage, { replace: true });
        return;
      } else {
        toast.error(res?.message ?? 'Login Failed');
        setStatus({ message: res?.message });
        return;
      }
    } catch (error) {
      if (error?.response?.data?.email_confirmation === false) {
        setShow(true);
      }
      toast.error(getErrorString(error));
      setStatus({ message: getErrorString(error) });
    } finally {
      setSubmitting(false);
      setEmail(data?.client_user?.email);
    }
  };

  return (
    <Authwrapper center={true}>
      <Formik
        innerRef={formikRef}
        initialValues={getInitialValues()}
        validationSchema={getValidationSchema}
        onSubmit={onSubmit}
      >
        {({ values, errors, touched, handleBlur, handleChange, handleSubmit, isSubmitting, status, setStatus }) => (
          <Flex vertical gap={36} justify="center" align="center">
            <img src={tenantData?.logo_url} width={160} />
            <Form autoComplete="on" onSubmit={handleSubmit} className={'authWrap'}>
              <Flex vertical gap={24} className={'authWrap'}>
                <Flex vertical justify="center" align="center" gap={8}>
                  <h2 className="text-align-center heading-text">{`Welcome to ${tenantData?.name ?? 'Elevate'}`}</h2>
                  <h5 className="text-align-center subheading-text">Login to your account to view your details</h5>
                </Flex>
                <Flex vertical gap={16}>
                  <Flex vertical style={{ marginBottom: '-12px' }}>
                    <GoogleSignIn handleLogin={handleLogin} fromPage={fromPage} />
                    <Divider style={{ color: 'var(--user-neutral-7)' }} className="body-14-medium" plain>
                      or
                    </Divider>
                  </Flex>
                  <Input
                    placeholder="you@example.com"
                    name="email"
                    autoComplete="username"
                    className={'h5-regular input-box'}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={(values?.email || '')?.replace(/\s/g, '')}
                    errorMsg={errors.email && touched.email && errors.email}
                  />
                  <Input
                    isPassword={true}
                    className={'h5-regular input-box'}
                    placeholder="Password"
                    name="password"
                    autoComplete="current-password"
                    iconRender={(visible) =>
                      visible ? <EyeOutlined className="eye-icon" /> : <EyeInvisibleOutlined className="eye-icon" />
                    }
                    onChange={(e) => !e.target.value?.includes(' ') && handleChange(e)}
                    onBlur={handleBlur}
                    onPressEnter={handleSubmit}
                    value={values?.password}
                    errorMsg={errors.password && touched.password && errors.password}
                  />
                  <Flex justify="space-between" align="center">
                    <Flex justify="center" align="center" gap={12}>
                      <Checkbox colorPrimar className="custom-checkbox" />
                      <div style={{ color: 'var(--user-main-text)' }} className="body-14-medium">
                        Remember me
                      </div>
                    </Flex>
                    <Link className="forgot-password" to={USER_FORGOT_PASSWORD_PATH}>
                      Forgot password?
                    </Link>
                  </Flex>
                </Flex>
                <Flex vertical gap={10}>
                  <Button
                    text={'Login'}
                    type="primary"
                    className={'h4-medium primary-button'}
                    size="large"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                  />
                  {show && <ResendCode email={email} />}
                </Flex>
                <Flex justify="center" align="center" gap={5}>
                  <div className="text-navigate">{`Don't have the account?`}</div>
                  <Link className="button-navigate" to={USER_SIGNUP_PATH}>
                    Sign Up
                  </Link>
                </Flex>
              </Flex>
            </Form>
          </Flex>
        )}
      </Formik>
    </Authwrapper>
  );
}
